import config from '@config';
import WithdrawalRequest from '@models/user/withdrawal/WithdrawalRequest';
import { WithdrawalType } from '@models/user/enums';
import History from '@models/user/history/History';
import ServerConfig from '@core/models/country/ServerConfig';
import CTRestRequestFactory from '@core/utils/network/CTRestRequestFactory';

export default class RestService {
    private readonly request: any;

    constructor(config: ServerConfig, restRequestFactory = CTRestRequestFactory) {
        this.request = restRequestFactory.create(
            config.user.host,
            true,
        );
    }

    /**
     * @throws {RequestError}
     */
    public uploadIdCard(file: string, fileName: string): Promise<any> {
        // readAsDataURL adds a prefix which is used for thumbnails. It has to be removed from the actual image
        // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
        const base64Content = file.substr(file.indexOf(',') + 1);
        return this.request({
            url: '/user/uploadDocumentVerification',
            method: 'post',
            data: {
                fileName,
                file: base64Content,
            },
            timeout: config.app.user.idCardVerification.uploadRequestTimeout
        });
    }

    /**
     * @throws {RequestError}
     */
    public updatePhone(phone: string): Promise<any> {
        return this.request({
            url: '/user/update',
            method: 'post',
            headers: {
                'Content-Type': 'application/json'
            },
            data: {
                phone
            },
        });
    }

    /**
     * @throws {RequestError}
     */
    public updatePrivacy(acceptsGeneralPrivacy: boolean, acceptsCustomPrivacy: boolean): Promise<any> {
        return this.request({
            url: '/user/updatePrivacyConsent',
            method: 'post',
            headers: {
                'Content-Type': 'application/json'
            },
            data: {
                generalPrivacy: acceptsGeneralPrivacy,
                customPrivacy: acceptsCustomPrivacy
            },
        });
    }

    /**
     * @throws {RequestError}
     */
    public getUserDetails(): Promise<any> {
        return this.request({
            url: '/user/getPlayerDetails',
            method: 'get',
        });
    }

    /**
     * @throws {RequestError}
     */
    public getPlayerBalance(): Promise<any> {
        return this.request({
            url: '/user/getPlayerBalance',
            method: 'get',
        });
    }

    /**
     * @throws {RequestError}
     */
    public createDeposit(amount: number, paySafeCard: boolean): Promise<any> {
        return this.request({
            url: '/user/createOnlineDeposit',
            method: 'post',
            data: {
                amount,
                psc: paySafeCard,
            }
        });
    }

    /**
     * @throws {RequestError}
     */
    public createOnlineWithdrawal(withdrawalRequest: WithdrawalRequest): Promise<any> {
        const data: any = {
            amount: withdrawalRequest.amount,
            paymentInstrumentType: withdrawalRequest.paymentInstrumentType,
            psc: withdrawalRequest.psc
        };

        if (withdrawalRequest.psc) {
            data.dateOfBirth = withdrawalRequest.dateOfBirth;
            data.firstName = withdrawalRequest.firstName;
            data.lastName = withdrawalRequest.lastName;
            data.email = withdrawalRequest.email;
        }

        return this.request({
            data,
            url: '/user/createOnlineWithdrawal',
            method: 'post',
        });
    }

    /**
     * @throws {RequestError}
     */
    public checkWithdrawalAbility(): Promise<any> {
        return this.request({
            url: '/user/checkWithdrawalEligibility',
            method: 'get',
        });
    }

    /**
     * @throws {RequestError}
     */
    public createShopWithdrawal(withdrawalRequest: WithdrawalRequest): Promise<any> {
        return this.request({
            url: '/user/shopWithdrawal',
            method: 'post',
            data: {
                amount: withdrawalRequest.amount,
                shopId: withdrawalRequest.shopId,
                withdrawalType: withdrawalRequest.type === WithdrawalType.shop ? 'REGULAR' : 'INSTANT'
            }
        });
    }

    /**
     * @throws {RequestError}
     */
    public fetchTransactions(page: number, pageSize = config.app.user.history.pageSize): Promise<History[]> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        return this.request({
            url: `/user/getPlayerTransactions?start=${start}&limit=${pageSize}`,
            method: 'get',
        });
    }

    /**
     * @throws {RequestError}
     */
    public fetchCasinoPlays(page: number, pageSize = config.app.user.history.pageSize): Promise<History[]> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        return this.request({
            url: `/user/getGameHistory?start=${start}&limit=${pageSize}`,
            method: 'get',
        });
    }

    /**
     * @throws {RequestError}
     */
    public fetchBets(page: number, pageSize = config.app.user.history.pageSize): Promise<History[]> {
        // weird logic with start and limit on leapbit's part
        const start = page * pageSize;
        return this.request({
            url: `/user/getBetsHistory?start=${start}&limit=${pageSize}`,
            method: 'get',
        });
    }

    /**
     * @throws {RequestError}
     */
    public fetchMessages() : Promise<any> {
        return this.request({
            url: '/user/getPopupMessages',
            method: 'get'
        });
    }

    /**
     * @throws {RequestError}
     */
    public respondToMessage(actionId: number) : Promise<any> {
        return this.request({
            url: '/user/messageAction',
            method: 'post',
            data: {
                actionId,
                fetchMessages: true
            }
        });
    }

    /**
     * @throws {RequestError}
     */
    public cancelWithdrawal(history: History): Promise<any> {
        return this.request({
            url: '/user/cancelWithdrawal',
            method: 'post',
            data: {
                transactionId: history.id
            }
        });
    }

    /**
     * @throws {RequestError}
     */
    public sendSmsCode(): Promise<any> {
        return this.request({
            url: '/user/resendSmsCode',
            method: 'post',
        });
    }

    /**
     * @throws {RequestError}
     */
    public validateSmsCode(code: string): Promise<any> {
        return this.request({
            url: '/user/validateSmsCode',
            method: 'post',
            data: {
                smsCode: code
            }
        });
    }

    public getLottoFavorites(): Promise<any> {
        return this.request({
            url: 'user/getLotoFavorites',
        });
    }

    public setLottoFavorites(data: any) {
        return this.request({
            data: {
                favs: data,
            },
            url: 'user/setLotoFavorites',
            method: 'post',
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/user/_private/RestService.ts